import { useCallback, useEffect, useRef, useState } from 'react'
import {
  type AudioTrack,
  type AudioUploadResult
} from '../../api'
import type {
  AudioTrackLabelSaveInput,
  AudioTrackLabelSuggestion
} from '../../api/labels'
import type { SunoPrompt } from '../../api/suno'
import { type AudioUploadItem } from './AudioUploadDialog'

type UseAudioUploadOptions = {
  onApplyUploadLabels: (track: AudioTrack, labels: AudioTrackLabelSaveInput[]) => Promise<void>
  onFindUploadPromptMatch: (stylePrompt: string) => Promise<SunoPrompt | null>
  onPersistUploadGroups: (track: AudioTrack, groupIds: string[]) => Promise<AudioTrack>
  onPersistUploadPrompt: (track: AudioTrack, stylePrompt: string) => Promise<AudioTrack>
  onPersistUploadPromptMatch: (tracks: AudioTrack[], prompt: SunoPrompt) => Promise<AudioTrack[]>
  onRegisterMobileUploadAction?: ((action: (() => void) | null) => void) | undefined
  onSuggestUploadPromptLabels: (stylePrompt: string) => Promise<AudioTrackLabelSuggestion[]>
  onUploadComplete: () => Promise<void>
  onUploadFile: (
    file: File,
    groupIds: string[],
    stylePrompt: string | null,
    sunoPromptId: string | null,
    onProgress: (progress: number) => void
  ) => Promise<AudioUploadResult>
  onUploadStateChange: (uploading: boolean) => void
}

export const useAudioUpload = ({
  onApplyUploadLabels,
  onFindUploadPromptMatch,
  onPersistUploadGroups,
  onPersistUploadPrompt,
  onPersistUploadPromptMatch,
  onRegisterMobileUploadAction,
  onSuggestUploadPromptLabels,
  onUploadComplete,
  onUploadFile,
  onUploadStateChange
}: UseAudioUploadOptions) => {
  const [isOpen, setIsOpen] = useState(false)
  const [groupIds, setGroupIds] = useState<string[]>([])
  const [stylePrompt, setStylePrompt] = useState('')
  const [promptMatch, setPromptMatch] = useState<SunoPrompt | null>(null)
  const [promptMatchLoading, setPromptMatchLoading] = useState(false)
  const [promptMatchError, setPromptMatchError] = useState<string | null>(null)
  const [labelSuggestions, setLabelSuggestions] = useState<AudioTrackLabelSuggestion[]>([])
  const [selectedSuggestionIds, setSelectedSuggestionIds] = useState<string[]>([])
  const [acceptedLabels, setAcceptedLabels] = useState<AudioTrackLabelSaveInput[]>([])
  const [suggestionError, setSuggestionError] = useState<string | null>(null)
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)
  const [items, setItems] = useState<AudioUploadItem[]>([])
  const [dragging, setDragging] = useState(false)
  const uploadedTracksRef = useRef<AudioTrack[]>([])
  const uploadedTrackItemsRef = useRef<Array<{ itemId: string; track: AudioTrack }>>([])
  const acceptedLabelsRef = useRef<AudioTrackLabelSaveInput[]>([])
  const groupIdsRef = useRef<string[]>([])
  const promptMatchRef = useRef<SunoPrompt | null>(null)
  const stylePromptRef = useRef('')

  const resetUploadState = (): void => {
    setItems([])
    setGroupIds([])
    setStylePrompt('')
    setPromptMatch(null)
    setPromptMatchError(null)
    setPromptMatchLoading(false)
    setLabelSuggestions([])
    setSelectedSuggestionIds([])
    setAcceptedLabels([])
    setSuggestionError(null)
    uploadedTracksRef.current = []
    uploadedTrackItemsRef.current = []
    acceptedLabelsRef.current = []
    groupIdsRef.current = []
    promptMatchRef.current = null
    stylePromptRef.current = ''
  }

  const openUploadDialog = useCallback((): void => {
    resetUploadState()
    setIsOpen(true)
  }, [])

  useEffect(() => {
    onRegisterMobileUploadAction?.(openUploadDialog)

    return () => {
      onRegisterMobileUploadAction?.(null)
    }
  }, [onRegisterMobileUploadAction, openUploadDialog])

  const patchItem = (id: string, patch: Partial<AudioUploadItem>): void => {
    setItems((current) =>
      current.map((item) => item.id === id ? { ...item, ...patch } : item)
    )
  }

  const persistPromptForTrack = async (
    itemId: string,
    track: AudioTrack,
    nextStylePrompt: string
  ): Promise<AudioTrack> => {
    if (!nextStylePrompt || track.style_prompt === nextStylePrompt) {
      return track
    }

    try {
      const updatedTrack = await onPersistUploadPrompt(track, nextStylePrompt)
      uploadedTracksRef.current = uploadedTracksRef.current.map((currentTrack) =>
        currentTrack.id === updatedTrack.id ? updatedTrack : currentTrack
      )
      uploadedTrackItemsRef.current = uploadedTrackItemsRef.current.map((currentItem) =>
        currentItem.track.id === updatedTrack.id
          ? { ...currentItem, track: updatedTrack }
          : currentItem
      )

      return updatedTrack
    } catch (error) {
      patchItem(itemId, {
        statusText: error instanceof Error ? `Uploaded, prompt save failed: ${error.message}` : 'Uploaded, prompt save failed'
      })

      return track
    }
  }

  const persistCurrentPromptToUploadedTracks = async (): Promise<void> => {
    const nextStylePrompt = stylePromptRef.current.trim()

    if (!nextStylePrompt) {
      return
    }

    await Promise.all(uploadedTrackItemsRef.current.map(async ({ itemId, track }) => {
      await persistPromptForTrack(itemId, track, nextStylePrompt)
    }))
  }

  const replaceUploadedTracks = (updatedTracks: AudioTrack[]): void => {
    uploadedTracksRef.current = uploadedTracksRef.current.map((currentTrack) =>
      updatedTracks.find((track) => track.id === currentTrack.id) ?? currentTrack
    )
    uploadedTrackItemsRef.current = uploadedTrackItemsRef.current.map((currentItem) => ({
      ...currentItem,
      track: updatedTracks.find((track) => track.id === currentItem.track.id) ?? currentItem.track
    }))
  }

  const persistCurrentPromptMatchToUploadedTracks = async (): Promise<void> => {
    const prompt = promptMatchRef.current
    const trackItems = uploadedTrackItemsRef.current
    const tracks = trackItems.map((item) => item.track)

    if (!prompt || tracks.length === 0) {
      return
    }

    trackItems.forEach(({ itemId }) => {
      patchItem(itemId, {
        status: 'assigning',
        statusText: 'Linking library prompt'
      })
    })

    try {
      const updatedTracks = await onPersistUploadPromptMatch(tracks, prompt)
      replaceUploadedTracks(updatedTracks)
    } catch (error) {
      trackItems.forEach(({ itemId }) => {
        patchItem(itemId, {
          statusText: error instanceof Error ? `Uploaded, library link failed: ${error.message}` : 'Uploaded, library link failed'
        })
      })
    }
  }

  const sameGroupIds = (left: string[], right: string[]): boolean => {
    if (left.length !== right.length) {
      return false
    }

    return left.every((id) => right.includes(id))
  }

  const formatCompleteStatus = (labelCount: number, groupCount: number, groupSaveFailed: boolean): string => {
    const details = [
      labelCount > 0 ? `${labelCount} label${labelCount === 1 ? '' : 's'}` : null,
      groupCount > 0 && !groupSaveFailed ? `${groupCount} group${groupCount === 1 ? '' : 's'}` : null
    ].filter(Boolean)

    if (groupSaveFailed) {
      return details.length > 0
        ? `Queued with ${details.join(' and ')}, group assignment failed`
        : 'Queued, group assignment failed'
    }

    return details.length > 0
      ? `Queued with ${details.join(' and ')}`
      : 'Queued for inspection'
  }

  const persistGroupsForTrack = async (
    itemId: string,
    track: AudioTrack,
    nextGroupIds: string[]
  ): Promise<{ track: AudioTrack; failed: boolean }> => {
    const currentGroupIds = track.groups.map((group) => group.id)

    if (sameGroupIds(currentGroupIds, nextGroupIds)) {
      return { track, failed: false }
    }

    try {
      patchItem(itemId, {
        status: 'assigning',
        statusText: 'Assigning groups'
      })
      const updatedTrack = await onPersistUploadGroups(track, nextGroupIds)
      uploadedTracksRef.current = uploadedTracksRef.current.map((currentTrack) =>
        currentTrack.id === updatedTrack.id ? updatedTrack : currentTrack
      )
      uploadedTrackItemsRef.current = uploadedTrackItemsRef.current.map((currentItem) =>
        currentItem.track.id === updatedTrack.id
          ? { ...currentItem, track: updatedTrack }
          : currentItem
      )

      return { track: updatedTrack, failed: false }
    } catch (error) {
      patchItem(itemId, {
        statusText: error instanceof Error ? `Uploaded, group assignment failed: ${error.message}` : 'Uploaded, group assignment failed'
      })

      return { track, failed: true }
    }
  }

  const persistCurrentGroupsToUploadedTracks = async (): Promise<void> => {
    const nextGroupIds = groupIdsRef.current

    await Promise.all(uploadedTrackItemsRef.current.map(async ({ itemId, track }) => {
      await persistGroupsForTrack(itemId, track, nextGroupIds)
    }))
  }

  const closeUploadDialog = async (): Promise<void> => {
    await persistCurrentPromptToUploadedTracks()
    await persistCurrentPromptMatchToUploadedTracks()
    await persistCurrentGroupsToUploadedTracks()
    setIsOpen(false)
    resetUploadState()
    await onUploadComplete()
  }

  const checkPromptLibrary = async (): Promise<void> => {
    const nextStylePrompt = stylePrompt.trim()

    if (!nextStylePrompt) {
      setPromptMatch(null)
      promptMatchRef.current = null
      setPromptMatchError('Enter a Suno style prompt before checking the library.')
      return
    }

    setPromptMatchLoading(true)
    setPromptMatchError(null)

    try {
      const prompt = await onFindUploadPromptMatch(nextStylePrompt)
      setPromptMatch(prompt)
      promptMatchRef.current = prompt
      setPromptMatchError(prompt ? null : 'No exact Prompt Library match found.')
    } catch (error) {
      setPromptMatch(null)
      promptMatchRef.current = null
      setPromptMatchError(error instanceof Error ? error.message : 'Unable to check prompt library.')
    } finally {
      setPromptMatchLoading(false)
    }
  }

  const suggestLabels = async (): Promise<void> => {
    const nextStylePrompt = stylePrompt.trim()

    if (!nextStylePrompt) {
      return
    }

    setLoadingSuggestions(true)
    setSuggestionError(null)

    try {
      const suggestions = await onSuggestUploadPromptLabels(nextStylePrompt)
      setLabelSuggestions(suggestions)
      setSelectedSuggestionIds(suggestions.map((suggestion) => suggestion.label_id))

      if (suggestions.length === 0) {
        setSuggestionError('No label suggestions returned.')
      }
    } catch (error) {
      setLabelSuggestions([])
      setSelectedSuggestionIds([])
      setSuggestionError(error instanceof Error ? error.message : 'Failed to suggest labels.')
    } finally {
      setLoadingSuggestions(false)
    }
  }

  const applySelectedSuggestions = (): void => {
    const nextAcceptedLabels = labelSuggestions
      .filter((suggestion) => selectedSuggestionIds.includes(suggestion.label_id))
      .map((suggestion) => ({
        labelId: suggestion.label_id,
        source: 'prompt_llm' as const,
        confidence: suggestion.confidence,
        weight: suggestion.weight,
        reason: suggestion.reason
      }))

    setAcceptedLabels(nextAcceptedLabels)
    acceptedLabelsRef.current = nextAcceptedLabels
    setSuggestionError(
      nextAcceptedLabels.length > 0
        ? `Applied ${nextAcceptedLabels.length} label(s) to this upload batch.`
        : 'No labels selected.'
    )

    if (nextAcceptedLabels.length === 0) {
      return
    }

    void Promise.all(uploadedTrackItemsRef.current.map(async ({ itemId, track }) => {
      patchItem(itemId, {
        status: 'labeling',
        statusText: 'Applying labels'
      })

      try {
        const trackWithPrompt = await persistPromptForTrack(itemId, track, stylePromptRef.current.trim())
        await onApplyUploadLabels(trackWithPrompt, nextAcceptedLabels)
        patchItem(itemId, {
          status: 'complete',
          statusText: `Queued with ${nextAcceptedLabels.length} label${nextAcceptedLabels.length === 1 ? '' : 's'}`
        })
      } catch (error) {
        patchItem(itemId, {
          status: 'complete',
          statusText: error instanceof Error ? `Uploaded, labeling failed: ${error.message}` : 'Uploaded, labeling failed'
        })
      }
    }))
  }

  const uploadFiles = async (files: FileList | null): Promise<void> => {
    if (!files?.length) {
      return
    }

    const nextItems: AudioUploadItem[] = Array.from(files).map((file, index) => ({
      id: `${file.name}-${file.lastModified}-${file.size}-${index}`,
      file,
      name: file.name,
      progress: 0,
      size: file.size,
      status: 'queued',
      statusText: undefined
    }))
    setItems(nextItems)
    onUploadStateChange(true)

    try {
      for (const item of nextItems) {
        patchItem(item.id, {
          progress: 0,
          status: 'uploading',
          statusText: undefined
        })

        try {
          const nextStylePrompt = stylePromptRef.current.trim()
          const result = await onUploadFile(item.file, groupIdsRef.current, nextStylePrompt || null, promptMatchRef.current?.id ?? null, (progress) => {
            patchItem(item.id, { progress })
          })
          const skipped = result.skipped.find((skippedFile) => skippedFile.filename === item.name)
          const matched = result.matched.find((matchedFile) => matchedFile.filename === item.name)
          const uploadedTrack = result.items[0]

          if (skipped) {
            patchItem(item.id, {
              progress: 100,
              status: 'skipped',
              statusText: 'Duplicate skipped'
            })
          } else if (matched) {
            const matchedTrackLabel = `${matched.track.artist ? `${matched.track.artist} - ` : ''}${matched.track.title}`
            patchItem(item.id, {
              progress: 100,
              status: 'matched',
              statusText: `Existing: ${matchedTrackLabel}`
            })
          } else {
            uploadedTracksRef.current = uploadedTrack
              ? [...uploadedTracksRef.current, uploadedTrack]
              : uploadedTracksRef.current
            uploadedTrackItemsRef.current = uploadedTrack
              ? [...uploadedTrackItemsRef.current, { itemId: item.id, track: uploadedTrack }]
              : uploadedTrackItemsRef.current
            const trackWithPrompt = uploadedTrack
              ? await persistPromptForTrack(item.id, uploadedTrack, stylePromptRef.current.trim())
              : uploadedTrack
            const groupResult = trackWithPrompt
              ? await persistGroupsForTrack(item.id, trackWithPrompt, groupIdsRef.current)
              : { track: trackWithPrompt, failed: false }
            const labelsToApply = acceptedLabelsRef.current

            if (groupResult.track && labelsToApply.length > 0) {
              patchItem(item.id, {
                progress: 100,
                status: 'labeling',
                statusText: 'Applying labels'
              })
              try {
                await onApplyUploadLabels(groupResult.track, labelsToApply)
              } catch (labelError) {
                patchItem(item.id, {
                  progress: 100,
                  status: 'complete',
                  statusText: labelError instanceof Error ? `Uploaded, labeling failed: ${labelError.message}` : 'Uploaded, labeling failed'
                })
                continue
              }
            }

            patchItem(item.id, {
              progress: 100,
              status: 'complete',
              statusText: formatCompleteStatus(labelsToApply.length, groupIdsRef.current.length, groupResult.failed)
            })
          }
        } catch (error) {
          patchItem(item.id, {
            status: 'failed',
            statusText: error instanceof Error ? error.message : 'Upload failed'
          })
        }
      }
    } finally {
      onUploadStateChange(false)
    }
  }

  const clearPromptMatch = (): void => {
    setPromptMatch(null)
    promptMatchRef.current = null
    setPromptMatchError(null)
  }

  const updateGroupIds = (nextGroupIds: string[]): void => {
    setGroupIds(nextGroupIds)
    groupIdsRef.current = nextGroupIds
  }

  const updatePrompt = (nextPrompt: string): void => {
    stylePromptRef.current = nextPrompt
    setStylePrompt(nextPrompt)
    setPromptMatch(null)
    promptMatchRef.current = null
    setPromptMatchError(null)
    setLabelSuggestions([])
    setSelectedSuggestionIds([])
    setAcceptedLabels([])
    setSuggestionError(null)
    acceptedLabelsRef.current = []
  }

  return {
    dialogProps: {
      acceptedLabels,
      dragging,
      items,
      loadingPromptMatch: promptMatchLoading,
      loadingSuggestions,
      onApplySelectedSuggestions: applySelectedSuggestions,
      onCheckPromptLibrary: () => void checkPromptLibrary(),
      onClearPromptMatch: clearPromptMatch,
      onClose: () => void closeUploadDialog(),
      onFilesSelected: (files: FileList | null) => void uploadFiles(files),
      onGroupIdsChange: updateGroupIds,
      onPromptChange: updatePrompt,
      onSelectSuggestionIdsChange: setSelectedSuggestionIds,
      onSetDragging: setDragging,
      onSuggestLabels: () => void suggestLabels(),
      promptMatch,
      promptMatchError,
      selectedGroupIds: groupIds,
      selectedSuggestionIds,
      stylePrompt,
      suggestionError,
      suggestions: labelSuggestions
    },
    isOpen,
    openUploadDialog
  }
}
